import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { OutstandingJob, AgingBucket } from '@/types/finance';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface CreditControlTableProps {
  jobs: OutstandingJob[];
  agingBuckets: AgingBucket[];
  totalOutstanding: number;
  isLoading: boolean;
}

type AgingFilter = 'all' | '0-30' | '31-60' | '61-90' | '90+';

export const CreditControlTable = ({ 
  jobs, 
  agingBuckets, 
  totalOutstanding,
  isLoading 
}: CreditControlTableProps) => {
  const navigate = useNavigate();
  const { formatMoney: formatCurrency } = useCompanyLocale();
  const [filter, setFilter] = useState<AgingFilter>('all');

  const filteredJobs = jobs.filter(job => {
    if (filter === 'all') return true;
    const days = job.days_outstanding;
    switch (filter) {
      case '0-30': return days >= 0 && days <= 30;
      case '31-60': return days >= 31 && days <= 60;
      case '61-90': return days >= 61 && days <= 90;
      case '90+': return days > 90;
      default: return true;
    }
  });

  const filteredTotal = filteredJobs.reduce((sum, job) => sum + job.outstanding_amount, 0);

  const handleRowClick = (jobId: string) => {
    navigate(`/jobs/${jobId}`);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Credit Control</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 bg-muted animate-pulse rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Credit Control (Outstanding Receivables)
          </CardTitle>
          <div className="flex items-center gap-4">
            <Select value={filter} onValueChange={(v) => setFilter(v as AgingFilter)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Filter by age" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({jobs.length})</SelectItem>
                <SelectItem value="0-30">0-30 Days ({agingBuckets[0]?.count || 0})</SelectItem>
                <SelectItem value="31-60">31-60 Days ({agingBuckets[1]?.count || 0})</SelectItem>
                <SelectItem value="61-90">61-90 Days ({agingBuckets[2]?.count || 0})</SelectItem>
                <SelectItem value="90+">90+ Days ({agingBuckets[3]?.count || 0})</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Total Outstanding</p>
              <p className="text-lg font-bold text-warning tabular-nums">
                {formatCurrency(filter === 'all' ? totalOutstanding : filteredTotal)}
              </p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Aging Summary Badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {agingBuckets.map((bucket) => (
            <Badge 
              key={bucket.label}
              variant={bucket.min >= 90 ? 'destructive' : bucket.min >= 60 ? 'secondary' : 'outline'}
              className="cursor-pointer"
              onClick={() => {
                if (bucket.min === 0) setFilter('0-30');
                else if (bucket.min === 31) setFilter('31-60');
                else if (bucket.min === 61) setFilter('61-90');
                else setFilter('90+');
              }}
            >
              {bucket.label}: {formatCurrency(bucket.total)} ({bucket.count})
            </Badge>
          ))}
        </div>

        {filteredJobs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No outstanding receivables in this period</p>
          </div>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Close Date</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJobs.map((job) => (
                  <TableRow 
                    key={job.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(job.id)}
                  >
                    <TableCell className="font-medium">{job.job_number}</TableCell>
                    <TableCell>
                      <CustomerLink customerId={job.customer_id} name={job.customer_name} />
                    </TableCell>
                    <TableCell>
                      {job.job_date ? format(parseISO(job.job_date), 'dd MMM yyyy') : '-'}
                    </TableCell>
                    <TableCell className={cn(
                      "text-right font-medium",
                      job.days_outstanding > 90 && "text-destructive",
                      job.days_outstanding > 60 && job.days_outstanding <= 90 && "text-warning",
                    )}>
                      {job.days_outstanding}
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(job.invoice_total)}</TableCell>
                    <TableCell className="text-right text-success">
                      {formatCurrency(job.amount_paid)}
                    </TableCell>
                    <TableCell className="text-right font-medium text-warning">
                      {formatCurrency(job.outstanding_amount)}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={job.payment_status === 'unpaid' ? 'destructive' : 'secondary'}
                      >
                        {job.payment_status === 'unpaid' ? 'Unpaid' : 'Partial'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
